// ============================================================================
// Hooks TanStack Query — CARTES DE COMPETENCE (mig 264) : /api/competency-cards
// ============================================================================
// Hook = TanStack Query SEULEMENT. L'autorisation vit au SERVEUR : le SPA consomme
// `can` tel que rendu (GA-05) ; sans le droit, le numero n'est pas masque au rendu,
// il n'a jamais quitte la base (seul `hasCardNumber` arrive).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiForbiddenError, ApiServerError } from '@gs/api-client';
import type { CompetencyCardType } from '../lib/profile/competencyCardTypes';

export type CompetencyReviewStatus = 'pending' | 'approved' | 'rejected';

export interface CompetencyCard {
  id: string;
  cardTypeCode: string;
  cardNumber: string | null;
  hasCardNumber: boolean;
  issuedOn: string | null;
  expiresOn: string | null;
  reviewStatus: CompetencyReviewStatus;
  submittedBy: 'employee' | 'manager';
  reviewedAt: string | null;
  reviewedByEmail: string | null;
  rejectionNote: string | null;
  version: number;
}

export interface CompetencyCardsResponse {
  cards: CompetencyCard[];
  can: { manage: boolean; readNumber: boolean };
}

export interface CompetencyCardDraft {
  cardTypeCode: CompetencyCardType;
  cardNumber: string | null;
  issuedOn: string | null;
  expiresOn: string | null;
}

const ROOT = ['gest', 'competency-cards'] as const;
const meKey = [...ROOT, 'me'] as const;
const memberKey = (email: string) => [...ROOT, 'member', email] as const;

/** Code court d'erreur pour le message i18n (jamais un code brut a l'ecran, GA-21). */
export function competencyErrorCode(err: unknown): string {
  if (err instanceof ApiForbiddenError) return 'forbidden';
  if (err instanceof ApiServerError) {
    if (err.status === 404) return 'notfound';
    if (err.status === 400) return 'invalid';
    if (err.status === 409) return 'conflict';
  }
  return 'server';
}

/** `email` absent = SES cartes (/me). */
export function useCompetencyCards(email: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: email ? memberKey(email) : meKey,
    queryFn: () =>
      api.get<CompetencyCardsResponse>(
        email ? `/api/competency-cards/${encodeURIComponent(email)}` : '/api/competency-cards/me',
      ),
    enabled,
    retry: false,
  });
}

function useInvalidate(email: string) {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: memberKey(email) });
}

const base = (email: string) => `/api/competency-cards/${encodeURIComponent(email)}`;

export function useAddCompetencyCard(email: string) {
  const invalidate = useInvalidate(email);
  return useMutation({
    mutationFn: (draft: CompetencyCardDraft) => api.post<{ id: string }>(base(email), draft),
    onSuccess: invalidate,
  });
}

export function useUpdateCompetencyCard(email: string) {
  const invalidate = useInvalidate(email);
  return useMutation({
    mutationFn: ({ id, draft }: { id: string; draft: CompetencyCardDraft }) =>
      api.put<{ ok: boolean }>(`${base(email)}/${id}`, draft),
    onSuccess: invalidate,
  });
}

export function useRetireCompetencyCard(email: string) {
  const invalidate = useInvalidate(email);
  return useMutation({
    mutationFn: (id: string) => api.delete<{ ok: boolean }>(`${base(email)}/${id}`),
    onSuccess: invalidate,
  });
}

export function useReviewCompetencyCard(email: string) {
  const invalidate = useInvalidate(email);
  return useMutation({
    mutationFn: ({ id, action, rejectionNote }: { id: string; action: 'approve' | 'reject'; rejectionNote: string | null }) =>
      api.post<{ ok: boolean }>(`${base(email)}/${id}/review`, { action, rejectionNote }),
    onSuccess: invalidate,
  });
}
